import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, Clock, Brain, Heart, Star, Sparkles, GraduationCap, ExternalLink, Target, Users } from 'lucide-react';
import { usePageView } from '../utils/usePageView';

const AboutPage = () => {
  // Page view fires once per session (deduped). Auto-captures nav context.
  usePageView('about');
  // Scroll to top on mount — prevents carrying over scroll position from
  // the previous page (e.g. clicking "About" in the homepage footer would
  // otherwise land you at the bottom of the about page).
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const navigate = useNavigate();
  const handleNavigate = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-gradient-to-b from-amber-50 via-orange-50 to-pink-50">
      {/* Navigation */}
      <nav className="bg-white/80 shadow-sm p-4 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/assets/brlogo2.webp" alt="BrightReading Logo" className="h-10 w-auto object-contain" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              BrightReading
            </h1>
          </div>
          <button
            onClick={() => handleNavigate('/')}
            className="text-gray-600 hover:bg-gray-100 p-2 rounded-lg transition"
            title="Back to Home"
          >
            <Home size={24} />
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">

        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-purple-100 px-4 py-2 rounded-full text-purple-700 font-medium mb-4">
            <Sparkles size={18} />
            <span>Ages 1-5</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">About BrightReading</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            I translate decades of literacy research into simple, 10-minute daily activities any parent can follow.
          </p>
        </div>

        {/* Meet the Founder */}
        <div className="bg-white rounded-2xl shadow-md p-6 sm:p-8 mb-10">
          <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">Meet the Founder</h3>
          <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8 max-w-3xl mx-auto">
            <img
              src="/assets/founder-face.webp"
              alt="Luc, founder of BrightReading"
              loading="lazy"
              className="w-40 h-40 sm:w-48 sm:h-48 rounded-full object-cover shadow-lg border-4 border-purple-100 flex-shrink-0"
            />
            <div className="text-center sm:text-left flex-1">
              <h4 className="text-2xl font-bold text-gray-900 mb-1">Luc</h4>
              <p className="text-gray-500 font-medium mb-4">22 · No kids (yet!)</p>
              <p className="text-gray-700 leading-relaxed mb-3">
                Literacy rates in the U.S. are dropping fast — 67% of 4th graders can't read at grade level. Meanwhile, toddlers are spending hours a day on screens, and parents are being told that early education is something to outsource to apps, tablets, and classrooms.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                The research says the opposite. <span className="font-bold text-gray-900">Parents are the single most effective teachers for kids ages 1-5</span> — no app, no classroom, no YouTube channel comes close. Ten minutes a day with a parent who knows what to do beats hours of passive screen time.
              </p>
              <p className="text-gray-700 leading-relaxed">
                I built BrightReading to give parents their role back. No guilt about not being a teacher. No more handing the iPad over and hoping for the best. Just simple, science-backed 10-minute activities that cut screen time, build literacy, and actually involve you in your child's learning — the way it's supposed to be.
              </p>
            </div>
          </div>
        </div>

        {/* Mission - Compact */}
        <div className="bg-gradient-to-br from-purple-500 to-pink-500 text-white rounded-2xl p-4 sm:p-8 mb-10 shadow-lg">
          <h3 className="text-2xl font-bold mb-3 text-center">My Mission</h3>
          <p className="text-lg leading-relaxed max-w-2xl mx-auto text-center">
            Make evidence-based early literacy accessible to every family, regardless of teaching experience or background.
          </p>
        </div>

        {/* What Makes Us Different - 3 cards */}
        <div className="grid md:grid-cols-3 gap-5 mb-10">
          <div className="bg-white rounded-xl p-6 shadow-md text-center">
            <div className="bg-blue-100 w-14 h-14 rounded-full flex items-center justify-center mb-3 mx-auto">
              <Brain className="text-blue-600" size={28} />
            </div>
            <h4 className="font-bold text-gray-900 mb-2">Research Translation</h4>
            <p className="text-gray-600 text-sm">I study the science so you don't have to. Every activity is based on verified methods.</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-md text-center">
            <div className="bg-green-100 w-14 h-14 rounded-full flex items-center justify-center mb-3 mx-auto">
              <Users className="text-green-600" size={28} />
            </div>
            <h4 className="font-bold text-gray-900 mb-2">Parent-Friendly</h4>
            <p className="text-gray-600 text-sm">No teaching degree required. Clear scripts and step-by-step guidance make it simple.</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-md text-center">
            <div className="bg-purple-100 w-14 h-14 rounded-full flex items-center justify-center mb-3 mx-auto">
              <Target className="text-purple-600" size={28} />
            </div>
            <h4 className="font-bold text-gray-900 mb-2">Age-Appropriate</h4>
            <p className="text-gray-600 text-sm">Four curricula (1-2, 2-3, 3-4, 4-5) matched to developmental stages.</p>
          </div>
        </div>

        {/* How Each Activity Works - Compact */}
        <div className="bg-white rounded-2xl shadow-md p-6 mb-10">
          <div className="flex items-center gap-3 mb-5">
            <Clock className="text-blue-500" size={28} />
            <h3 className="text-xl font-bold text-gray-900">Every Activity: 10 Minutes, 4 Parts</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="bg-yellow-100 text-yellow-700 font-bold w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2">2m</div>
              <h5 className="font-bold text-gray-900 text-sm">Warm-Up</h5>
              <p className="text-gray-500 text-xs">Vocabulary & conversation</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 text-blue-700 font-bold w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2">4m</div>
              <h5 className="font-bold text-gray-900 text-sm">Story Time</h5>
              <p className="text-gray-500 text-xs">Interactive reading</p>
            </div>
            <div className="text-center">
              <div className="bg-green-100 text-green-700 font-bold w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2">3m</div>
              <h5 className="font-bold text-gray-900 text-sm">Sound Play</h5>
              <p className="text-gray-500 text-xs">Phonological games</p>
            </div>
            <div className="text-center">
              <div className="bg-purple-100 text-purple-700 font-bold w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2">1m</div>
              <h5 className="font-bold text-gray-900 text-sm">Celebration</h5>
              <p className="text-gray-500 text-xs">Print awareness & praise</p>
            </div>
          </div>
        </div>

        {/* Research Foundation - Compact */}
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 mb-10 border border-purple-200">
          <div className="text-center mb-5">
            <GraduationCap className="text-indigo-600 mx-auto mb-2" size={32} />
            <h3 className="text-xl font-bold text-gray-900">Built on Decades of Research</h3>
            <p className="text-sm text-gray-500 italic">I didn't conduct these studies — I compiled and translated them for parents.</p>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="bg-white rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">50+</div>
              <p className="text-xs text-gray-600">Studies Reviewed</p>
            </div>
            <div className="bg-white rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-purple-600">30+</div>
              <p className="text-xs text-gray-600">Researchers</p>
            </div>
            <div className="bg-white rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-pink-600">40+</div>
              <p className="text-xs text-gray-600">Years of Evidence</p>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => handleNavigate('/research')}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg transition"
            >
              <span>View Research Bibliography</span>
              <ExternalLink size={18} />
            </button>
          </div>
        </div>

        {/* Why I Built BrightReading */}
        <div className="bg-white rounded-2xl shadow-md p-6 mb-10 text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Why I Built BrightReading</h3>
          <div className="max-w-2xl mx-auto rounded-xl overflow-hidden shadow-lg">
            <video
              controls
              playsInline
              preload="none"
              poster="/assets/founder-poster.webp"
              className="w-full aspect-video bg-black"
            >
              <source src="/assets/foundervideo.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>

        {/* Designed for Real Life */}
        <div className="bg-white rounded-2xl shadow-md p-6 mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Heart className="text-pink-500" size={28} />
            <h3 className="text-xl font-bold text-gray-900">Designed for Real Life</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex gap-3">
              <div className="text-2xl">📱</div>
              <div>
                <h4 className="font-bold text-gray-900 text-sm">Scripted Guidance</h4>
                <p className="text-gray-600 text-xs">Exact words to say — no guesswork</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="text-2xl">⏱️</div>
              <div>
                <h4 className="font-bold text-gray-900 text-sm">10-15 Minutes</h4>
                <p className="text-gray-600 text-xs">5 days per week fits any schedule</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="text-2xl">📊</div>
              <div>
                <h4 className="font-bold text-gray-900 text-sm">Progress Tracking</h4>
                <p className="text-gray-600 text-xs">See your child's growth week by week</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="text-2xl">🏆</div>
              <div>
                <h4 className="font-bold text-gray-900 text-sm">Motivation System</h4>
                <p className="text-gray-600 text-xs">Streaks and achievements keep everyone engaged</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 text-white rounded-2xl shadow-lg p-5 sm:p-10 text-center">
          <Star className="mx-auto mb-3" size={40} />
          <h3 className="text-2xl font-bold mb-3">Start Your Family's Reading Journey</h3>
          <p className="text-lg mb-6 opacity-95 max-w-xl mx-auto">
            Join parents building strong literacy foundations, one activity at a time.
          </p>
          <button
            onClick={() => handleNavigate('/login')}
            className="bg-white text-purple-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-50 transition shadow-lg hover:shadow-2xl transform hover:scale-105"
          >
            Get Started Today
          </button>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
